import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound } from "lucide-react";

const ROLES = [
  { key: "admin", label: "Administración", desc: "Acceso total a todas las sedes" },
  { key: "staff", label: "Recepción", desc: "Gestión completa sin usuarios ni planes" },
  { key: "franquiciado", label: "Franquiciatario", desc: "Solo sus sedes; no crea planes de pago" },
  { key: "profesor", label: "Profesor", desc: "Consulta, avances y asistencia" },
];

const empty = { nombre: "", email: "", password: "", role: "staff", sede_ids: [] };

export default function Usuarios() {
  const [items, setItems] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [reset, setReset] = useState(null);
  const [nueva, setNueva] = useState("");

  const guardarPassword = async () => {
    if (nueva.length < 6) return toast.error("La contraseña necesita al menos 6 caracteres");
    try {
      const { data } = await api.put(`/usuarios/${reset.id}/password`, { nueva });
      toast.success(data.email_enviado
        ? `Contraseña actualizada y enviada por email a ${reset.nombre}`
        : "Contraseña actualizada (el email no se pudo enviar)");
      setReset(null);
      setNueva("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const load = () => api.get("/usuarios").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/sedes").then(({ data }) => setSedes(data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSede = (id) =>
    set("sede_ids", form.sede_ids.includes(id) ? form.sede_ids.filter((x) => x !== id) : [...form.sede_ids, id]);
  const sedeName = (id) => sedes.find((s) => s.id === id)?.nombre || "";

  const save = async () => {
    try {
      await api.post("/usuarios", form);
      toast.success("Usuario creado");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/usuarios/${id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="usuarios-page">
      <PageHeader
        title="Usuarios internos"
        subtitle="Administración, recepción, franquiciatarios y profesores"
        action={
          <Button className="rounded-full font-semibold" onClick={() => setOpen(true)} data-testid="add-usuario-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuevo usuario
          </Button>
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {items.map((u) => (
          <Card key={u.id} className="rounded-2xl p-6 border-border shadow-sm" data-testid={`usuario-card-${u.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-display font-bold text-lg truncate">{u.nombre}</h3>
                <p className="text-xs text-muted-foreground mt-1 truncate">{u.email}</p>
              </div>
              <Badge className="shrink-0">{ROLES.find((r) => r.key === u.role)?.label || u.role}</Badge>
            </div>
            {u.role === "franquiciado" && (
              <p className="text-sm text-muted-foreground mt-3" data-testid={`usuario-sedes-${u.id}`}>
                Sedes: {(u.sede_ids || []).map(sedeName).filter(Boolean).join(" · ") || "—"}
              </p>
            )}
            <div className="flex gap-2 mt-6">
              <Button variant="outline" size="sm" className="rounded-lg"
                onClick={() => { setReset({ id: u.id, nombre: u.nombre }); setNueva(""); }}
                data-testid={`reset-password-${u.id}`}>
                <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Restablecer contraseña
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg text-destructive"
                onClick={() => remove(u.id)} data-testid={`delete-usuario-${u.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!reset} onOpenChange={(v) => !v && setReset(null)}>
        <DialogContent className="rounded-2xl" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Restablecer contraseña</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Escribe la nueva contraseña de <b>{reset?.nombre}</b>. Se la enviaremos por email y
            podrá cambiarla desde "Mi contraseña".
          </p>
          <div className="grid gap-2">
            <Label>Nueva contraseña</Label>
            <Input type="text" value={nueva} onChange={(e) => setNueva(e.target.value)}
              placeholder="Mínimo 6 caracteres" data-testid="reset-password-input" />
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={guardarPassword}
              data-testid="reset-password-save">Guardar y enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="usuario-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuevo usuario</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="usuario-nombre-input" />
            </div>
            <div className="grid gap-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="usuario-email-input" />
            </div>
            <div className="grid gap-2">
              <Label>Contraseña</Label>
              <PasswordInput value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="usuario-password-input" />
            </div>
            <div className="grid gap-2">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="usuario-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{ROLES.find((r) => r.key === form.role)?.desc}</p>
            </div>
            {form.role === "franquiciado" && (
              <div className="grid gap-2">
                <Label>Sedes asignadas</Label>
                <div className="flex flex-wrap gap-2">
                  {sedes.map((s) => (
                    <button key={s.id} type="button" onClick={() => toggleSede(s.id)}
                      data-testid={`usuario-sede-${s.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                        form.sede_ids.includes(s.id)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:bg-muted"
                      }`}>
                      {s.nombre}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={save} data-testid="save-usuario-btn">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
